import { useEffect, useState } from 'react'
import { sgBaseUrl } from '@fs/zion-config'
import { useUser } from '@fs/zion-user'
import axios from '../utils/giveAxios'

const url = `${sgBaseUrl}/service/ident/cas/cas-public-api/authorizations`

// Keep permissions in sync with https://github.com/fs-eng/identity-cas/blob/master/cas-bootstrap-web/src/main/resources/bootstrap.xml#L1030
const cisAdminApiContext = {
  activate: 'CIS_ACTIVATE',
  cudUserAccount: 'CIS_CUD_USER_ACCOUNT',
  enableDisable: 'CIS_ENABLE_DISABLE',
  infoClassConfidential: 'CIS_INFO_CLASS_CONFIDENTIAL_PERM',
  infoClassInternal: 'CIS_INFO_CLASS_INTERNAL_USE_PERM',
  infoClassLimited: 'CIS_INFO_CLASS_LIMITED_ACCESS_PERM',
  infoClassPublic: 'CIS_INFO_CLASS_PUBLIC_PERM',
  profileDataCud: 'CIS_PROFILE_DATA_CUD_PERM',
  profileDataRead: 'CIS_PROFILE_DATA_READ_PERM',
  readAccount: 'CIS_READ_ACCOUNT',
  readAccountType: 'CIS_READ_ACCOUNT_TYPE',
  readUserDetail: 'CIS_READ_USER_DETAIL',
  recoverPassword: 'CIS_RECOVER_PASSWORD_PERM',
  recoverUsername: 'CIS_RECOVER_USERNAME_PERM',
}

// Keep permissions in sync with https://github.com/fs-eng/identity-authn/blob/master/api/src/main/resources/IdentityCasData.xml#L287
const identityAdminContext = {
  flagAccountCud: 'AccountManagerFlagAccountCUDPermission',
  flagAccountRead: 'AccountManagerFlagAccountReadPermission',
  multiFactorAuth: 'AccountManagerAccessPermission',
  readChangeLogs: 'AccountManagerReadChangeLogsPermission',
  verificationIgnoreRateLimits: 'VerificationIgnoreRateLimits',
  verificationSend: 'VerificationSendPermission',
}

const userProfileContext = {
  editUserProfile: 'EditUserProfileName',
}

const activatePermissions = [cisAdminApiContext.activate]

const authPermissions = [identityAdminContext.multiFactorAuth]

const changeLogPermissions = [identityAdminContext.readChangeLogs]

const deletePermissions = [cisAdminApiContext.cudUserAccount]

const editPermissions = [cisAdminApiContext.profileDataCud]

const editProfilePermissions = [userProfileContext.editUserProfile]

const enableDisablePermissions = [cisAdminApiContext.enableDisable]

const flagPermissions = [identityAdminContext.flagAccountCud, identityAdminContext.flagAccountRead]

const passwordResetPermissions = [
  cisAdminApiContext.recoverPassword,
  cisAdminApiContext.recoverUsername,
  identityAdminContext.verificationIgnoreRateLimits,
  identityAdminContext.verificationSend,
]

const searchPermissions = [
  // Confidential access needed for return accountType in search.
  // Internal access needed for username search
  // Limited access needed for email/name/sms search
  cisAdminApiContext.infoClassConfidential,
  cisAdminApiContext.infoClassInternal,
  cisAdminApiContext.infoClassLimited,
  cisAdminApiContext.infoClassPublic,
  cisAdminApiContext.readAccount,
  cisAdminApiContext.readAccountType,
]

const viewPermissions = [cisAdminApiContext.profileDataRead, cisAdminApiContext.readUserDetail]

function getFeatures(data) {
  const authorizationResults = data?.results || []
  const unauthorizedPermissions = authorizationResults.flatMap((x) => x.unauthorizedPermissions)
  const unauthorized = (permission) => unauthorizedPermissions.includes(permission)

  return {
    // User has to have authenticated with high enough assurance level to enable auth routes (i.e. via multi-factor authentication).
    // Also, by design some of the other permissions are not granted unless multi-factor auth has occurred.
    activateEnabled: !activatePermissions.some(unauthorized),
    authEnabled: !authPermissions.some(unauthorized),
    changeLogEnabled: !changeLogPermissions.some(unauthorized),
    enableDisableEnabled: !enableDisablePermissions.some(unauthorized),
    deleteEnabled: !deletePermissions.some(unauthorized),
    editEnabled: !editPermissions.some(unauthorized),
    flagEnabled: !flagPermissions.some(unauthorized),
    passwordResetEnabled: !passwordResetPermissions.some(unauthorized),
    searchEnabled: !searchPermissions.some(unauthorized),
    viewEnabled: !viewPermissions.some(unauthorized),
    editProfileEnabled: !editProfilePermissions.some(unauthorized),
  }
}

export default function useReportsAccess() {
  const [data, setData] = useState()
  const [error, setError] = useState()
  const [loading, setLoading] = useState(true)
  const { signedIn, userLoading } = useUser()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: authData } = await axios.post(url, {
          authorizationRequests: [
            {
              context: 'CIS_ADMIN_API',
              permissions: [
                'CIS_ACTIVATE',
                'CIS_CUD_USER_ACCOUNT',
                'CIS_ENABLE_DISABLE',
                'CIS_INFO_CLASS_CONFIDENTIAL_PERM',
                'CIS_INFO_CLASS_INTERNAL_USE_PERM',
                'CIS_INFO_CLASS_LIMITED_ACCESS_PERM',
                'CIS_INFO_CLASS_PUBLIC_PERM',
                'CIS_PROFILE_DATA_CUD_PERM',
                'CIS_PROFILE_DATA_READ_PERM',
                'CIS_READ_ACCOUNT',
                'CIS_READ_ACCOUNT_TYPE',
                'CIS_READ_USER_DETAIL',
                'CIS_RECOVER_PASSWORD_PERM',
                'CIS_RECOVER_USERNAME_PERM',
              ],
            },
            {
              context: 'IdentityAdminContext',
              permissions: [
                'AccountManagerFlagAccountCUDPermission',
                'AccountManagerFlagAccountReadPermission',
                'AccountManagerAccessPermission',
                'AccountManagerReadChangeLogsPermission',
                'VerificationIgnoreRateLimits',
                'VerificationSendPermission',
              ],
            },
            {
              context: 'UserProfile',
              permissions: ['EditUserProfileName'],
            },
          ],
        })
        setData(getFeatures(authData))
      } catch (err) {
        setError(err)
      } finally {
        setLoading(false)
      }
    }
    if (signedIn && !userLoading) fetchData()
  }, [signedIn, userLoading])

  return { data, error, loading, authEnabled: data?.authEnabled || false, searchEnabled: data?.searchEnabled || false }
}
